import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useSite } from "@/context/SiteContext";

const LINKS = [
  { id: "story", zh: "团队故事", en: "Story" },
  { id: "leadership", zh: "管理层", en: "Our Team", page: "/leadership" },
  { id: "members", zh: "我们的伙伴", en: "Our Partners", page: "/members" },
  { id: "album", zh: "团队相册", en: "Album", page: "/album" },
  { id: "services", zh: "我们的服务", en: "Services", page: "/services" },
  { id: "praise", zh: "客户表扬", en: "Praise" },
  { id: "faq", zh: "常见问题", en: "FAQ", page: "/faq/what-is-isi-group" },
  { id: "contact", zh: "联系我们", en: "Contact", page: "/contact" },
];

export const Navbar = ({ onNav }) => {
  const { lang, setLang } = useSite();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed top-0 inset-x-0 z-50 backdrop-blur-xl border-b transition-[background-color,box-shadow] duration-500 ${
        scrolled ? "bg-white/80 border-slate-200/60 shadow-[0_4px_24px_rgb(15,23,42,0.06)]" : "bg-white/40 border-white/20"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => onNav("#top")}
          className="flex items-center gap-2.5 font-display font-extrabold text-lg text-slate-900 whitespace-nowrap shrink-0"
        >
          <img src="/logo.png" alt="i.S.i Group" className="h-9 w-auto object-contain" />
          i.S.i Group
        </button>

        <nav className="hidden md:flex items-center gap-7">
          {LINKS.map((l) =>
            l.page ? (
              <a
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                href={l.page}
                className="text-sm font-semibold text-slate-600 hover:text-orange-500 transition-colors duration-300"
              >
                {lang === "zh" ? l.zh : l.en}
              </a>
            ) : (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => onNav(`#${l.id}`)}
                className="text-sm font-semibold text-slate-600 hover:text-orange-500 transition-colors duration-300"
              >
                {lang === "zh" ? l.zh : l.en}
              </button>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <div data-testid="lang-toggle" className="flex items-center rounded-full bg-slate-100 p-1 border border-slate-200">
            {["zh", "en"].map((l) => (
              <button
                key={l}
                data-testid={`lang-${l}`}
                onClick={() => setLang(l)}
                className={`px-3 py-1 rounded-full text-xs font-bold transition-[background-color,color] duration-300 ${
                  lang === l ? "bg-orange-500 text-white shadow-sm" : "text-slate-500 hover:text-slate-800"
                }`}
              >
                {l === "zh" ? "中文" : "EN"}
              </button>
            ))}
          </div>
          <button
            data-testid="mobile-menu-button"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Menu"
            className="md:hidden w-9 h-9 grid place-items-center rounded-xl bg-slate-100 border border-slate-200 text-slate-700 hover:bg-sky-100 transition-colors duration-300"
          >
            {menuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="md:hidden border-t border-slate-200/60 bg-white/95 backdrop-blur-xl shadow-[0_16px_32px_rgb(15,23,42,0.08)]"
          >
            <div className="px-5 py-3 flex flex-col">
              {LINKS.map((l) =>
                l.page ? (
                  <a
                    key={l.id}
                    data-testid={`mobile-nav-${l.id}`}
                    href={l.page}
                    onClick={() => setMenuOpen(false)}
                    className="py-3 px-3 rounded-xl text-sm font-bold text-slate-700 hover:bg-sky-50 hover:text-sky-600 transition-colors duration-200"
                  >
                    {lang === "zh" ? l.zh : l.en}
                  </a>
                ) : (
                  <button
                    key={l.id}
                    data-testid={`mobile-nav-${l.id}`}
                    onClick={() => { setMenuOpen(false); onNav(`#${l.id}`); }}
                    className="py-3 px-3 rounded-xl text-left text-sm font-bold text-slate-700 hover:bg-sky-50 hover:text-sky-600 transition-colors duration-200"
                  >
                    {lang === "zh" ? l.zh : l.en}
                  </button>
                )
              )}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
